/**
 * RequestAccessPage — public self-service access request form (B-2026-0027)
 *
 * URL: /request-access  (public — no auth required)
 *
 * States:
 *  1. Form  — user fills email, display name, role, optional message + Turnstile
 *  2. Success — "Thank you, we'll be in touch" confirmation (always shown on 200)
 *
 * Error handling (per B-2026-0024 reliability lessons — never silently swallow):
 *  - 400 captcha_failed → inline captcha error + widget reset
 *  - 422 validation     → show field / generic validation message
 *  - 429 rate limit     → visible rate-limit banner
 *  - Other errors       → visible generic error banner
 *
 * Visual shell mirrors RegisterPage.tsx (split-screen, brand glow, Space Grotesk
 * headings) so the auth system looks cohesive.
 */

import { useState, useRef, useEffect, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, UserIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { requestAccess } from '@/services/registrationRequestsApi';
import Button from '@/components/Button';

// ── Turnstile globals (mirrors RegisterPage.tsx) ──────────────────────────────

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

declare global {
  interface Window {
    turnstile?: {
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      render: (container: string | HTMLElement, params: Record<string, unknown>) => string;
    };
  }
}

// ── Error extraction (mirrors RegisterPage.tsx) ───────────────────────────────

function extractApiError(error: unknown): { code: string | null; message: string } {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: { detail?: string | { error?: string; message?: string }; error?: string };
      };
    };
    const data = axiosError.response?.data;

    if (data?.detail && typeof data.detail === 'object') {
      const nested = data.detail as { error?: string; message?: string };
      return {
        code: nested.error ?? null,
        message: nested.message ?? 'Something went wrong. Please try again.',
      };
    }

    const code = data?.error ?? null;
    const detail = data?.detail;
    if (typeof detail === 'string') return { code, message: detail };

    const status = axiosError.response?.status;
    if (status === 429) {
      return { code: 'rate_limited', message: 'Too many requests. Please wait a moment and try again.' };
    }
    if (status === 422) {
      return { code: 'validation', message: 'Please check your entries and try again.' };
    }
  }
  if (error instanceof Error) return { code: null, message: error.message };
  return { code: null, message: 'Something went wrong. Please try again.' };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        border: '2px solid var(--color-border-strong)',
        borderTopColor: 'var(--color-brand-primary)',
        animation: 'ra-spin 0.75s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

function AlertBanner({
  children,
  kind = 'error',
}: {
  children: React.ReactNode;
  kind?: 'error' | 'info';
}) {
  const colors =
    kind === 'error'
      ? {
          border: 'rgba(248,113,113,0.30)',
          bg: 'rgba(248,113,113,0.08)',
          text: 'var(--color-signal-bad)',
        }
      : {
          border: 'rgba(96,165,250,0.30)',
          bg: 'rgba(96,165,250,0.08)',
          text: 'var(--color-signal-info)',
        };
  return (
    <div
      role="alert"
      style={{
        borderRadius: '10px',
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bg,
        padding: '12px 14px',
        fontSize: '13px',
        color: colors.text,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

// ── Shell (mirrors RegisterPage.tsx's AuthShell exactly) ──────────────────────

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <style>{`
        @keyframes ra-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ra-form-wrap {
          animation: ra-fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .ra-input-focus input:focus,
        .ra-input-focus input:focus-visible,
        .ra-input-focus textarea:focus,
        .ra-input-focus textarea:focus-visible {
          outline: none;
          border-color: var(--color-brand-primary);
          box-shadow: 0 0 0 2px rgba(59,130,246,0.22);
        }
        .ra-submit-btn {
          transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;
        }
        .ra-submit-btn:not(:disabled):hover {
          background-color: var(--color-brand-primary-hover);
          box-shadow: 0 0 18px rgba(59,130,246,0.30), 0 4px 12px rgba(0,0,0,0.20);
        }
        .ra-submit-btn:not(:disabled):active {
          transform: scale(0.98);
        }
        @keyframes ra-spin { to { transform: rotate(360deg); } }
      `}</style>

      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          backgroundColor: 'var(--color-surface-base)',
        }}
      >
        {/* ── Left panel (desktop only) ─────────────────────────── */}
        <div
          className="hidden lg:flex"
          style={{
            width: '50%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--color-surface-base)',
            padding: '64px 56px',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '560px',
              height: '560px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.06) 45%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: '-60px',
              left: '-40px',
              width: '360px',
              height: '360px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '28px',
              maxWidth: '380px',
            }}
          >
            <img
              src="/pwa-512.png"
              alt="PointeTracer logo"
              style={{
                width: '160px',
                height: '160px',
                objectFit: 'contain',
                borderRadius: '32px',
                boxShadow:
                  '0 0 48px rgba(59,130,246,0.28), 0 8px 32px rgba(0,0,0,0.35)',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h1
                className="text-display-lg"
                style={{
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  letterSpacing: '-0.5px',
                }}
              >
                Movement analysis, measured.
              </h1>
              <p
                className="text-body-md"
                style={{ color: 'var(--color-text-secondary)', margin: 0 }}
              >
                Request access to PointeTracer.
              </p>
            </div>
          </div>
        </div>

        {/* ── Right panel (form / state) ────────────────────────── */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 24px',
            backgroundColor: 'var(--color-surface-raised-2)',
          }}
          className="lg:w-1/2"
        >
          <div className="ra-form-wrap w-full" style={{ maxWidth: '400px' }}>
            {/* Mobile-only logo */}
            <div
              className="flex lg:hidden"
              style={{ justifyContent: 'center', marginBottom: '32px' }}
            >
              <img
                src="/pwa-512.png"
                alt="PointeTracer logo"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  boxShadow: '0 0 24px rgba(59,130,246,0.25)',
                }}
              />
            </div>

            {children}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Success screen ────────────────────────────────────────────────────────────

function SuccessScreen() {
  return (
    <AuthShell>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Icon */}
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            backgroundColor: 'rgba(30,215,96,0.12)',
            border: '1px solid rgba(30,215,96,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckCircleIcon
            style={{ width: '28px', height: '28px', color: 'var(--color-accent)' }}
            aria-hidden="true"
          />
        </div>

        {/* Heading */}
        <div>
          <h2
            className="text-display-lg"
            style={{
              color: 'var(--color-text-primary)',
              margin: '0 0 8px',
              letterSpacing: '-0.5px',
            }}
          >
            Request received
          </h2>
          <p className="text-body-md" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            Thank you for your interest.
          </p>
        </div>

        {/* Info card */}
        <div
          style={{
            borderRadius: '12px',
            border: '1px solid var(--color-border-subtle)',
            backgroundColor: 'var(--color-surface-raised-3)',
            padding: '18px',
          }}
        >
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.7 }}
          >
            If approved, you&apos;ll receive an invitation email with a link to complete your
            registration.
          </p>
        </div>

        {/* Back link */}
        <p
          className="text-body-sm"
          style={{ color: 'var(--color-text-muted)', margin: 0 }}
        >
          Already have an account?{' '}
          <Link
            to="/login"
            style={{
              color: 'var(--color-brand-primary)',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Sign in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}

// ── Request form ──────────────────────────────────────────────────────────────

const MESSAGE_MAX = 500;

function RequestForm({ onSuccess }: { onSuccess: () => void }) {
  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'athlete' | 'coach'>('athlete');
  const [message, setMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Explicit Turnstile render — mirrors RegisterPage.tsx's polling pattern
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const container = turnstileContainerRef.current;
    if (!container) return;

    let widgetId: string | null = null;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || !container) return;
      try {
        widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (tkn: string) => setTurnstileToken(tkn),
          'error-callback': () => setTurnstileToken(''),
          'expired-callback': () => setTurnstileToken(''),
          theme: 'dark',
        });
        if (widgetId) container.dataset.turnstileWidgetId = widgetId;
      } catch (err) {
         
        console.warn('Turnstile render failed:', err);
      }
    };

    if (window.turnstile) {
      render();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 100);
      setTimeout(() => clearInterval(interval), 15000);
    }

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const turnstileRequired = TURNSTILE_SITE_KEY.length > 0;
  const turnstileReady = !turnstileRequired || turnstileToken.length > 0;
  const messageOver = message.length > MESSAGE_MAX;

  const isFormValid =
    email.trim().length > 0 &&
    displayName.trim().length > 0 &&
    !messageOver &&
    turnstileReady;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;

    if (turnstileRequired && !turnstileToken) {
      setError('Please complete the CAPTCHA verification before submitting.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const payload = {
        email: email.trim(),
        display_name: displayName.trim(),
        requested_role: role,
        ...(message.trim() ? { message: message.trim() } : {}),
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      };

      await requestAccess(payload);
      onSuccess();
    } catch (err) {
      const { code, message: errMsg } = extractApiError(err);

      if (code === 'captcha_failed' && window.turnstile && turnstileContainerRef.current) {
        const widgetId = turnstileContainerRef.current.dataset.turnstileWidgetId;
        if (widgetId) window.turnstile.reset(widgetId);
        setTurnstileToken('');
        setError('CAPTCHA verification failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: '44px',
    borderRadius: '10px',
    border: '1px solid var(--color-border-strong)',
    backgroundColor: 'var(--color-surface-raised-3)',
    color: 'var(--color-text-primary)',
    fontSize: '14px',
    padding: '0 40px 0 14px',
    boxSizing: 'border-box',
    transition: 'border-color 180ms ease, box-shadow 180ms ease',
  };

  return (
    <div>
      {/* Heading */}
      <div style={{ marginBottom: '32px' }}>
        <h2
          className="text-display-lg"
          style={{
            color: 'var(--color-text-primary)',
            margin: '0 0 8px',
            letterSpacing: '-0.5px',
          }}
        >
          Request access
        </h2>
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          An admin will review your request and send an invitation if approved.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
      >
        {/* Error banner */}
        {error && <AlertBanner kind="error">{error}</AlertBanner>}

        {/* Email */}
        <div className="ra-input-focus">
          <label
            htmlFor="ra-email"
            className="text-label"
            style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '6px' }}
          >
            Email
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="ra-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              disabled={isSubmitting}
              style={inputStyle}
            />
            <EnvelopeIcon
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: 'var(--color-text-muted)',
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Display name */}
        <div className="ra-input-focus">
          <label
            htmlFor="ra-display-name"
            className="text-label"
            style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '6px' }}
          >
            Your name
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="ra-display-name"
              type="text"
              autoComplete="name"
              placeholder="How should we address you?"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              disabled={isSubmitting}
              style={inputStyle}
            />
            <UserIcon
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: 'var(--color-text-muted)',
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Role radio */}
        <div>
          <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend
              className="text-label"
              style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '8px' }}
            >
              I am a…
            </legend>
            <div style={{ display: 'flex', gap: '8px' }}>
              {(['athlete', 'coach'] as const).map((r) => {
                const selected = role === r;
                return (
                  <label
                    key={r}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      height: '44px',
                      borderRadius: '10px',
                      border: `1px solid ${selected ? 'var(--color-brand-primary)' : 'var(--color-border-strong)'}`,
                      backgroundColor: selected
                        ? 'rgba(59,130,246,0.10)'
                        : 'var(--color-surface-raised-3)',
                      color: selected ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
                      fontSize: '14px',
                      fontWeight: selected ? 600 : 500,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'all 150ms ease',
                    }}
                  >
                    <input
                      type="radio"
                      name="ra-role"
                      value={r}
                      checked={selected}
                      onChange={() => setRole(r)}
                      disabled={isSubmitting}
                      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                    />
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>

        {/* Optional message */}
        <div className="ra-input-focus">
          <label
            htmlFor="ra-message"
            className="text-label"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              color: 'var(--color-text-muted)',
              marginBottom: '6px',
            }}
          >
            <span>
              Message{' '}
              <span style={{ fontSize: '12px', fontWeight: 400 }}>(optional)</span>
            </span>
            <span
              style={{
                fontSize: '12px',
                color: messageOver ? 'var(--color-signal-bad)' : 'var(--color-text-muted)',
              }}
            >
              {message.length}/{MESSAGE_MAX}
            </span>
          </label>
          <div style={{ position: 'relative' }}>
            <textarea
              id="ra-message"
              placeholder="Tell us a bit about yourself or how you plan to use PointeTracer…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MESSAGE_MAX + 50}
              disabled={isSubmitting}
              rows={3}
              style={{
                width: '100%',
                borderRadius: '10px',
                border: `1px solid ${messageOver ? 'var(--color-signal-bad)' : 'var(--color-border-strong)'}`,
                backgroundColor: 'var(--color-surface-raised-3)',
                color: 'var(--color-text-primary)',
                fontSize: '14px',
                padding: '10px 36px 10px 14px',
                boxSizing: 'border-box',
                resize: 'vertical',
                minHeight: '80px',
                lineHeight: 1.5,
                fontFamily: 'inherit',
                transition: 'border-color 180ms ease, box-shadow 180ms ease',
              }}
            />
            <ChatBubbleBottomCenterTextIcon
              style={{
                position: 'absolute',
                right: '12px',
                top: '12px',
                width: '16px',
                height: '16px',
                color: 'var(--color-text-muted)',
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
          </div>
          {messageOver && (
            <p style={{ fontSize: '12px', color: 'var(--color-signal-bad)', margin: '4px 0 0' }}>
              Message must be {MESSAGE_MAX} characters or fewer.
            </p>
          )}
        </div>

        {/* Turnstile */}
        {TURNSTILE_SITE_KEY && (
          <div>
            <div ref={turnstileContainerRef} />
            {turnstileRequired && !turnstileToken && (
              <p
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  margin: '4px 0 0',
                  lineHeight: 1.4,
                }}
              >
                Complete the verification above to continue.
              </p>
            )}
          </div>
        )}

        {/* Submit */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full ra-submit-btn"
          style={{
            borderRadius: '10px',
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: '15px',
            letterSpacing: '0.1px',
            marginTop: '4px',
          }}
          disabled={isSubmitting || !isFormValid}
        >
          {isSubmitting ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Spinner size={16} />
              Submitting…
            </span>
          ) : (
            'Request access'
          )}
        </Button>
      </form>

      {/* Back link */}
      <div
        style={{
          marginTop: '28px',
          borderTop: '1px solid var(--color-border-subtle)',
          paddingTop: '20px',
        }}
      >
        <p
          className="text-body-sm"
          style={{ color: 'var(--color-text-muted)', textAlign: 'center', margin: 0 }}
        >
          Already have an account?{' '}
          <Link
            to="/login"
            style={{
              color: 'var(--color-brand-primary)',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function RequestAccessPage() {
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return <SuccessScreen />;
  }

  return (
    <AuthShell>
      <RequestForm onSuccess={() => setSubmitted(true)} />
    </AuthShell>
  );
}
